'use client';

import { Box, SxProps } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_ID = 'google-recaptcha-v2';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

type GreCaptcha = {
  ready?: (_callback: () => void) => void;
  render: (
    _container: HTMLElement,
    _parameters: {
      sitekey: string;
      callback: (_token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => number;
  reset: (_widgetId?: number) => void;
};

declare global {
  interface Window {
    grecaptcha?: GreCaptcha;
  }
}

let scriptPromise: Promise<void> | null = null;

/**
 * Loads the reCAPTCHA script once per page, no matter how many widgets are mounted, and
 * resolves only once the API is ready to render.
 */
function loadRecaptchaScript(): Promise<void> {
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

      // The script may already have loaded (an earlier mount, or fast refresh), in which case
      // its load event has been and gone - waiting for another one would hang forever.
      if (existing?.dataset.loaded === 'true' || window.grecaptcha?.render) {
        resolve();
        return;
      }

      const script = existing ?? document.createElement('script');

      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', () => {
        scriptPromise = null;
        script.remove();
        reject(
          new Error('reCAPTCHA script did not load (blocked by the network or an extension?)')
        );
      });

      if (!existing) {
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
  }

  // grecaptcha.render can exist before the API is fully initialised; ready() is the documented
  // way to wait for it, and skipping it is a known source of render-time exceptions.
  return scriptPromise.then(
    () =>
      new Promise<void>((resolve) => {
        if (typeof window.grecaptcha?.ready === 'function') {
          window.grecaptcha.ready(() => resolve());
        } else {
          resolve();
        }
      })
  );
}

export type RecaptchaHandle = {
  /** Clears the current token and returns the widget to its unchecked state. */
  reset: () => void;
};

type RecaptchaProps = {
  siteKey: string;
  /** Called with the token when solved, and with null when it expires or errors. */
  onChange: (_token: string | null) => void;
  /** Called when the widget can't be loaded at all (blocked script, bad key, offline). */
  onError?: () => void;
  sx?: SxProps;
};

/**
 * reCAPTCHA v2 ("I'm not a robot") checkbox. The token is verified server-side by the
 * broker platform, which is what actually blocks scripted posts to the public endpoint.
 */
export const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(function Recaptcha(
  { siteKey, onChange, onError, sx },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  // Latest callbacks, so re-renders don't require re-rendering the widget.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.reset(widgetIdRef.current);
        onChangeRef.current(null);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;

    loadRecaptchaScript()
      .then(() => {
        // Guard against React's double-invoked effects rendering two widgets.
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;

        if (!window.grecaptcha?.render) {
          throw new Error('reCAPTCHA loaded but grecaptcha.render is unavailable');
        }

        // Rendering into a node that already holds a widget throws; a remount can leave one
        // behind, so clear it first.
        containerRef.current.innerHTML = '';

        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current(null),
          'error-callback': () => onChangeRef.current(null),
        });
      })
      .catch((error) => {
        console.error(
          '[RECAPTCHA] Widget failed to render. Check that NEXT_PUBLIC_RECAPTCHA_SITE_KEY is the ' +
            'reCAPTCHA v2 SITE key (not the secret key) and that this domain is allowed for that ' +
            'key pair. Underlying error:',
          error
        );
        if (!cancelled) onErrorRef.current?.();
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  return <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center', ...sx }} />;
});
